import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader, CalendarClock, AlertCircle } from 'lucide-react';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { kanbanApiService } from '../../services/kanbanApi';
import {
  KANBAN_CADENCE_TYPES,
  KANBAN_CADENCE_FREQUENCIES,
  CADENCE_TYPE_CONFIG,
  type KanbanCadence,
  type KanbanCadenceType,
  type KanbanCadenceFrequency,
} from '../../types/kanban';
import { cn } from '../../utils/cn';

function formatDatetimeLocal(ms: number | null): string {
  if (ms === null || ms === undefined) return '';
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

interface KanbanCadenceModalProps {
  isOpen: boolean;
  onClose: () => void;
  boardId: string;
  cadence?: KanbanCadence | null;
  onSaved: () => void;
}

export const KanbanCadenceModal = ({
  isOpen,
  onClose,
  boardId,
  cadence = null,
  onSaved,
}: KanbanCadenceModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(modalRef, onClose);

  const isEdit = Boolean(cadence);

  const [title, setTitle] = useState('');
  const [cadenceType, setCadenceType] = useState<KanbanCadenceType>('standup');
  const [frequency, setFrequency] = useState<KanbanCadenceFrequency>('weekly');
  const [intervalDays, setIntervalDays] = useState('');
  const [description, setDescription] = useState('');
  const [nextOccurrence, setNextOccurrence] = useState('');
  const [calendarEventId, setCalendarEventId] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch fresh cadence data when editing to ensure we have latest values
  useEffect(() => {
    if (!isOpen || !cadence) return;
    void kanbanApiService.getCadence(cadence.id).catch(console.error);
  }, [isOpen, cadence]);

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setIsSubmitting(false);
    if (cadence) {
      setTitle(cadence.title);
      setCadenceType(cadence.cadence_type);
      setFrequency(cadence.frequency);
      setIntervalDays(
        cadence.interval_days === null || cadence.interval_days === undefined
          ? ''
          : String(cadence.interval_days),
      );
      setDescription(cadence.description ?? '');
      setNextOccurrence(formatDatetimeLocal(cadence.next_occurrence));
      setCalendarEventId(cadence.calendar_event_id ?? '');
      setIsActive(Boolean(cadence.is_active));
    } else {
      setTitle('');
      setCadenceType('standup');
      setFrequency('weekly');
      setIntervalDays('');
      setDescription('');
      setNextOccurrence('');
      setCalendarEventId('');
      setIsActive(true);
    }
  }, [isOpen, cadence]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      setError('Title is required.');
      return;
    }

    let nextMs: number | null = null;
    if (nextOccurrence.trim()) {
      const parsed = new Date(nextOccurrence).getTime();
      if (Number.isNaN(parsed)) {
        setError('Invalid next occurrence date.');
        return;
      }
      nextMs = parsed;
    }

    let interval: number | null = null;
    if (frequency === 'custom') {
      const n = Number(intervalDays);
      if (intervalDays.trim() === '' || Number.isNaN(n) || n < 1) {
        setError('Interval days is required for custom frequency (minimum 1).');
        return;
      }
      interval = Math.floor(n);
    }

    setIsSubmitting(true);
    setError(null);

    try {
      if (cadence) {
        await kanbanApiService.updateCadence(cadence.id, {
          title: trimmedTitle,
          cadence_type: cadenceType,
          frequency,
          interval_days: frequency === 'custom' ? interval : null,
          description: description.trim() || null,
          next_occurrence: nextMs,
          calendar_event_id: calendarEventId.trim() || null,
          is_active: isActive,
        });
      } else {
        await kanbanApiService.createCadence(boardId, {
          title: trimmedTitle,
          cadence_type: cadenceType,
          frequency,
          interval_days: frequency === 'custom' ? interval : null,
          description: description.trim() || null,
          next_occurrence: nextMs,
          calendar_event_id: calendarEventId.trim() || null,
          is_active: isActive,
        });
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save cadence.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="modal-overlay z-[9999] overflow-y-auto">
      <div
        ref={modalRef}
        className="modal-panel max-w-lg"
      >
        <button
          type="button"
          onClick={onClose}
          className="modal-close z-10"
          aria-label="Close"
          disabled={isSubmitting}
        >
          <X className="w-5 h-5" />
        </button>

        <div className="modal-header">
          <h2 className="modal-title flex items-center gap-2">
            <CalendarClock className="w-5 h-5 text-accent-400 shrink-0" />
            {isEdit ? 'Edit cadence' : 'New cadence'}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="alert alert-danger" role="alert">
              <AlertCircle />
              <span>{error}</span>
            </div>
          )}

          <div>
            <label htmlFor="cadence-title" className="text-xs font-medium text-glass-400 mb-1.5 block">
              Title
            </label>
            <input
              id="cadence-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="glass-input w-full"
              disabled={isSubmitting}
              required
            />
          </div>

          <div>
            <label htmlFor="cadence-type" className="text-xs font-medium text-glass-400 mb-1.5 block">
              Cadence type
            </label>
            <select
              id="cadence-type"
              value={cadenceType}
              onChange={(e) => setCadenceType(e.target.value as KanbanCadenceType)}
              className="glass-input w-full"
              disabled={isSubmitting}
            >
              {KANBAN_CADENCE_TYPES.map((t) => (
                <option key={t} value={t}>
                  {CADENCE_TYPE_CONFIG[t].label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="cadence-frequency" className="text-xs font-medium text-glass-400 mb-1.5 block">
              Frequency
            </label>
            <select
              id="cadence-frequency"
              value={frequency}
              onChange={(e) => setFrequency(e.target.value as KanbanCadenceFrequency)}
              className="glass-input w-full"
              disabled={isSubmitting}
            >
              {KANBAN_CADENCE_FREQUENCIES.map((f) => (
                <option key={f} value={f}>
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                </option>
              ))}
            </select>
          </div>

          {frequency === 'custom' ? (
            <div>
              <label htmlFor="cadence-interval" className="text-xs font-medium text-glass-400 mb-1.5 block">
                Interval (days)
              </label>
              <input
                id="cadence-interval"
                type="number"
                min={1}
                value={intervalDays}
                onChange={(e) => setIntervalDays(e.target.value)}
                className="glass-input w-full"
                disabled={isSubmitting}
              />
            </div>
          ) : null}

          <div>
            <label htmlFor="cadence-desc" className="text-xs font-medium text-glass-400 mb-1.5 block">
              Description
            </label>
            <textarea
              id="cadence-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="glass-input w-full min-h-[72px] resize-y"
              rows={3}
              disabled={isSubmitting}
            />
          </div>

          <div>
            <label htmlFor="cadence-next" className="text-xs font-medium text-glass-400 mb-1.5 block">
              Next occurrence
            </label>
            <input
              id="cadence-next"
              type="datetime-local"
              value={nextOccurrence}
              onChange={(e) => setNextOccurrence(e.target.value)}
              className="glass-input w-full"
              disabled={isSubmitting}
            />
          </div>

          <div>
            <label htmlFor="cadence-cal" className="text-xs font-medium text-glass-400 mb-1.5 block">
              Calendar event ID
            </label>
            <input
              id="cadence-cal"
              type="text"
              value={calendarEventId}
              onChange={(e) => setCalendarEventId(e.target.value)}
              className="glass-input w-full font-mono"
              disabled={isSubmitting}
            />
          </div>

          <label className="flex items-center gap-2 text-sm text-glass-200 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="form-checkbox"
              disabled={isSubmitting}
            />
            Active
          </label>

          <div className="modal-footer">
            <button type="button" onClick={onClose} className="glass-button-secondary" disabled={isSubmitting}>
              Cancel
            </button>
            <button
              type="submit"
              className={cn('btn-primary', isSubmitting && 'opacity-70')}
              disabled={isSubmitting}
            >
              {isSubmitting ? <Loader className="w-4 h-4 animate-spin" /> : null}
              {isEdit ? 'Save' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};
